/**
 * ActingLeaderBar - the GM's one question when a kill-team's brothers stand together on a stage without their leader
 * (Squad Mode stage 7, canvas board 7): "Who leads Theo and Castiel here?", a button per brother. Asked once for that
 * set on that stage (`engine/killTeam/actingLeader`); the GM's alone. Draws nothing when nothing is owed.
 */
import { useContext } from 'react';
import { useKillTeamsContext } from '../../contexts/KillTeamsContext';
import RosterContext from '../../contexts/RosterContext';
import SessionContext from '../../contexts/SessionContext';
import { answerAsk, leaderAsks, type LeaderAsk } from '../../engine/killTeam/actingLeader';
import type { Combatant } from '../../engine/roster/types';
import common from '../../styles/common.module.css';
import styles from '../../styles/CallForTest.module.css';

/** "Theo and Castiel", "Theo, Castiel and Ludwig". */
const namesOf = (names: readonly string[]) =>
  names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : (names[0] ?? '');

function Ask({ ask, onPick }: { ask: LeaderAsk; onPick: (id: string) => void }) {
  const without = ask.leaderName ? `without ${ask.leaderName}` : 'without its leader';
  return (
    <div className={styles.callLine} data-testid={`acting-leader-${ask.tag}`}>
      <span>{`${ask.label} formed ${without}. Who leads ${namesOf(ask.brothers.map((b) => b.name))} here?`}</span>
      {ask.brothers.map((b) => (
        <button
          key={b.id}
          type="button"
          className={common.btnSmall}
          data-testid={`acting-leader-pick-${b.id}`}
          onClick={() => onPick(b.id)}
        >
          {b.name}
        </button>
      ))}
    </div>
  );
}

export default function ActingLeaderBar() {
  const k = useKillTeamsContext();
  const roster = (useContext(RosterContext) as { roster?: Combatant[] } | null)?.roster;
  const session = useContext(SessionContext) as { isGm?: boolean; sessionId?: string | null } | null;
  const gm = !!session?.isGm || !session?.sessionId;
  if (!k || !roster || !gm) return null;
  const asks = leaderAsks(roster, k.teams, k.sceneId);
  if (!asks.length) return null;
  return (
    <div className={styles.calls} role="status" data-testid="acting-leader-bar">
      {asks.map((ask) => (
        <Ask key={ask.key} ask={ask} onPick={(id) => k.setDoc(answerAsk(k.doc, ask, id))} />
      ))}
    </div>
  );
}
